import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { FaRegFilePdf, FaRegTrashAlt } from 'react-icons/fa'

import './ModalNfDevolucao.css'
import {
  Btn,
  BtnIcon,
  ConfirmDialog,
  CustomModal,
  ImageContainer,
  InputText,
  Loading,
  UploadFileBtn,
} from '../../../../../components'
import { showMessageBox } from '../../../../../store/actions/system.actions'
import { errorHandler } from '../../../../../services/util/error.service'
import * as RetornoHttp from '../../../../../services/api/https/deliveries/retorno.http'
import { getDelivery } from '../../../../../services/api/https/deliveries/delivery.http'
import {
  atualizarDeliveryInvoicePathAndNumber,
  newAssocNotaFiscalClienteWithDeliveryStep,
} from '../../../../../services/api/https/ordem-coleta/ordem-coleta.http'
import {
  deleteFileNotaFiscalDevolucaoParcial,
  getNotasFiscaisDevolucaoParcialByStep,
} from '../../../../../services/api/https/processos/nf-devolucao-parcial.http'
import {
  urlUploadNfDevolucaoParcial,
  urlUploadNfDevolucaoTotal,
} from '../../../../../services/api/routes/receive-vehicle.routes'

export const STATUS_COM_NF_DEVOLUCAO = ['Devolução Parcial', 'Devolução Total']

// Mesmo fluxo do TP Drive: a foto vai para o S3 pelo Docs Repository (pastas nf_devolucao_total e
// nf_devolucao_parcial). Na Devolução Total o caminho e o número ficam na própria entrega; na
// Parcial, cada nota é um registro de NF do cliente associado ao trecho da entrega.
function ModalNfDevolucao({ show, entrega, onModalClose, dispatch }) {
  // Vars
  const isTotal = !!entrega && entrega.status === 'Devolução Total'

  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [numero, setNumero] = useState('')
  const [notas, setNotas] = useState([])
  const [notaParaApagar, setNotaParaApagar] = useState(null)
  const [versao, setVersao] = useState(null)

  useEffect(() => {
    if (!show || !entrega) return

    let cancelado = false
    const getNotas = async () => {
      try {
        setIsLoading(true)
        setNumero('')
        setNotas([])
        setVersao(null)

        if (isTotal) {
          const delivery = await getDelivery(entrega.delivery_id)
          if (cancelado) return
          setNumero(delivery.invoice_number || '')
          setNotas(buildNotasDevolucaoTotal(delivery))
        } else {
          const fetchedNotas = await getNotasFiscaisDevolucaoParcialByStep(entrega.delivery_step_id)
          if (cancelado) return
          setNotas(fetchedNotas)
        }
      } catch (error) {
        if (!cancelado) dispatch(showMessageBox(null, errorHandler(error)))
      } finally {
        if (!cancelado) setIsLoading(false)
      }
    }
    getNotas()

    return () => {
      cancelado = true
    }
  }, [show, entrega])

  // Events
  const onUploadCompleted = async path => {
    try {
      if (isTotal) {
        const delivery = await atualizarDeliveryInvoicePathAndNumber(
          entrega.delivery_id,
          path,
          numero,
        )
        setNotas(buildNotasDevolucaoTotal(delivery))
      } else {
        const nota = await newAssocNotaFiscalClienteWithDeliveryStep(
          entrega.delivery_step_id,
          numero,
          path,
        )
        setNotas([...notas.filter(n => n.id !== nota.id), nota])
        setNumero('')
      }
      // O S3 sobrescreve a chave quando a nota é anexada de novo: sem a versão o browser
      // continuaria exibindo a foto anterior do cache.
      setVersao(Date.now())
      dispatch(showMessageBox('Nota Fiscal de Devolução anexada com sucesso', null))
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    }
  }

  const onBtnSalvarNumeroClicked = async () => {
    try {
      setIsSaving(true)
      const path = notas.length > 0 ? notas[0].invoice_path : null
      const delivery = await atualizarDeliveryInvoicePathAndNumber(
        entrega.delivery_id,
        path,
        numero,
      )
      setNotas(buildNotasDevolucaoTotal(delivery))
      dispatch(showMessageBox('Número da Nota Fiscal salvo com sucesso', null))
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    } finally {
      setIsSaving(false)
    }
  }

  const onApagarConfirmado = async () => {
    const nota = notaParaApagar
    setNotaParaApagar(null)
    try {
      const file_name = getFileName(nota.invoice_path)
      if (isTotal) {
        await atualizarDeliveryInvoicePathAndNumber(entrega.delivery_id, null, nota.invoice_number)
        await RetornoHttp.deleteDeliveryNfDevolucaoTotalFile(file_name)
        setNotas([])
      } else {
        await deleteFileNotaFiscalDevolucaoParcial(file_name, nota.id, entrega.delivery_step_id)
        setNotas(notas.filter(n => n.id !== nota.id))
      }
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    }
  }

  // Helpers
  const numeroJaAnexado = !isTotal && notas.some(n => n.invoice_number === numero)
  const uploadDisabled = !numero || numeroJaAnexado

  const getUploadUrl = () => {
    if (!entrega) return ''
    return isTotal
      ? urlUploadNfDevolucaoTotal(entrega.delivery_id)
      : urlUploadNfDevolucaoParcial(entrega.client_id, numero)
  }

  const getAviso = () => {
    if (numeroJaAnexado) return 'Esta nota já está anexada. Apague a atual para substituir a foto.'
    if (!numero) return 'Informe o número da nota para anexar a foto.'
    return null
  }

  // Render
  const aviso = getAviso()
  return (
    <>
      <CustomModal
        header={
          entrega
            ? `NF de ${entrega.status} - ${entrega.identifier} - ${entrega.nomeFantasia}`
            : 'NF de Devolução'
        }
        isVisible={show}
        onClose={onModalClose}
        width={700}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      >
        {isLoading ? (
          <div className="text-center">
            <Loading />
          </div>
        ) : (
          <div className="p-grid">
            <div className="p-col-12">
              <div className="ModalNfDevolucao__form">
                <div className="ModalNfDevolucao__numero">
                  <InputText
                    label="Nº da Nota Fiscal de Devolução"
                    value={numero}
                    onChange={value => setNumero(value.replace(/\D/g, ''))}
                    marginTop={0}
                    inputMode="numeric"
                    maxLength={20}
                    name="numero_nf_devolucao"
                  />
                </div>
                {isTotal && (
                  <Btn
                    isLoading={isSaving}
                    disabled={!numero}
                    onClick={onBtnSalvarNumeroClicked}
                    style={{ padding: '8px 14px' }}
                  >
                    Salvar Número
                  </Btn>
                )}
                {/* O disabled do FileUpload só bloqueia a escolha do arquivo: com um arquivo já
                    escolhido o clique ainda enviaria, por isso o bloqueio do clique aqui. */}
                <div className={`ModalNfDevolucao__upload${uploadDisabled ? ' is-disabled' : ''}`}>
                  <UploadFileBtn
                    disabled={uploadDisabled}
                    label="Anexar Foto"
                    name={isTotal ? 'nf-dev-total' : 'nf-dev-parcial'}
                    url={getUploadUrl()}
                    onUploadCompleted={onUploadCompleted}
                  />
                </div>
              </div>
              {aviso && <div className="ModalNfDevolucao__aviso">{aviso}</div>}
            </div>
            {notas.map(nota => (
              <div className="p-col-12" key={nota.id || nota.invoice_path}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Nota Fiscal {nota.invoice_number || '(sem número)'}
                </div>
                {isPdf(nota.invoice_path) ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <a href={nota.invoice_path} target="_blank" rel="noopener noreferrer">
                      <FaRegFilePdf size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      Abrir PDF
                    </a>
                    <div style={{ marginLeft: 12 }}>
                      <BtnIcon onClick={() => setNotaParaApagar(nota)}>
                        <FaRegTrashAlt color="red" size={18} />
                      </BtnIcon>
                    </div>
                  </div>
                ) : (
                  <ImageContainer
                    imagePath={versao ? `${nota.invoice_path}?v=${versao}` : nota.invoice_path}
                    alt={`Nota Fiscal de Devolução ${nota.invoice_number}`}
                    onBtnDeleteClicked={() => setNotaParaApagar(nota)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </CustomModal>

      <ConfirmDialog
        isVisible={!!notaParaApagar}
        title="Atenção"
        okLabel="Apagar"
        cancelLabel="Cancelar"
        content={
          <span>
            A foto da Nota Fiscal {notaParaApagar ? notaParaApagar.invoice_number : ''} será
            apagada.
            <br />
            Prosseguir?
          </span>
        }
        onCancelar={() => setNotaParaApagar(null)}
        onOk={onApagarConfirmado}
      />
    </>
  )
}

function buildNotasDevolucaoTotal({ path_nf_dev_total, invoice_number }) {
  return path_nf_dev_total ? [{ invoice_number, invoice_path: path_nf_dev_total }] : []
}

function getFileName(path) {
  const path_array = path.split('/')
  return path_array[path_array.length - 1]
}

function isPdf(path) {
  return /\.pdf$/i.test(path)
}

export default connect()(ModalNfDevolucao)
